import { TypedDocumentNode, gql } from '@apollo/client';
import QueryString from 'qs';
import { FormattedMessage } from 'react-intl';
import { generatePath, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import essenceOpening from '@sorare/core/src/assets/rivals/essence-opening.png';
import Button from '@sorare/core/src/atoms/buttons/Button';
import StarBall from '@sorare/core/src/atoms/icons/StarBall';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { DisplayL, LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import { FOOTBALL_CRAFT_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { GridBackground } from 'components/rivals/GridBackground';
import { BackgroundAligned } from 'components/rivals/GridBackground/BackgroundAligned';

import { EssenceOpeningOnboardingDialog_probabilisticBundleSlotCardShardItem } from './__generated__/index.graphql';

const StyledDialog = styled(Dialog)`
  @media ${tabletAndAbove} {
    height: min(100vh - 8 * var(--unit), 812px);
  }
`;

const Content = styled(Vertical).attrs({ gap: 4 })`
  isolation: isolate;
  position: absolute;
  padding: var(--quadruple-unit) var(--double-unit);
  bottom: var(--double-unit);
  @media ${tabletAndAbove} {
    position: static;
    height: 50%;
    justify-content: flex-end;
  }
`;
const StyledStarBall = styled(StarBall)`
  margin-top: -40px;
`;
const StyledBackgroundAligned = styled(BackgroundAligned)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--unit);
`;
const Badge = styled(Horizontal).attrs({ gap: 0, center: true })`
  border-radius: var(--double-unit);
  width: min-content;
  padding: 2px var(--unit);
  border: 1px solid var(--c-rivals-reputation);
  background: linear-gradient(
      -25deg,
      rgba(var(--c-rgb-rivals-reputation), 0.15) 50%,
      transparent 50%
    )
    padding-box;
  align-self: center;
`;

const Img = styled.img`
  filter: drop-shadow(0px 0px 30px rgba(var(--c-rgb-rivals-reputation), 0.5));
`;

type Props = {
  open: boolean;
  onClose: () => void;
  shardSlotItem: EssenceOpeningOnboardingDialog_probabilisticBundleSlotCardShardItem;
  disableOnClose?: boolean;
};

// TODO: get this from backend
export const LIMITED_TIER_5_THRESHOLD = 20;

export const EssenceOpeningOnboardingDialog = ({
  open,
  onClose,
  shardSlotItem,
  disableOnClose,
}: Props) => {
  const canCraft = shardSlotItem.quantity >= LIMITED_TIER_5_THRESHOLD;
  const [searchParams] = useSearchParams();
  return (
    <StyledDialog
      open={open}
      onClose={disableOnClose ? undefined : onClose}
      maxWidth="xs"
    >
      <GridBackground
        color="#f1ff00"
        height="50%"
        bgColor="var(--c-neutral-200)"
      >
        <StyledBackgroundAligned
          horizontalDelta={0}
          verticalDelta={-0.36}
          maxWidth={2}
        >
          <Img src={essenceOpening} alt="" width={160} />
          <Badge>
            <LabelL
              bold
              color="var(--c-rivals-reputation)"
            >{`x${shardSlotItem.quantity}`}</LabelL>
          </Badge>
        </StyledBackgroundAligned>
      </GridBackground>
      <Content gap={4}>
        <Vertical gap={1}>
          <LabelM>
            <FormattedMessage
              id="EssenceOpeningOnboardingDialog.subtitle"
              defaultMessage="You won"
            />
          </LabelM>
          <DisplayL color="var(--c-rivals-reputation)">
            <FormattedMessage
              id="EssenceOpeningOnboardingDialog.title"
              defaultMessage="Limited<br></br>Essence"
              values={{
                br: () => <br />,
              }}
            />
            <StyledStarBall color="var(--c-rivals-reputation" width={16} />
          </DisplayL>
          <LabelM>
            {canCraft ? (
              <FormattedMessage
                id="CraftCardDialog.description"
                defaultMessage="You accumulated enough Essence to craft a powerful Limited Card in the Card Factory"
              />
            ) : (
              <FormattedMessage
                id="EssenceOpeningOnboardingDialog.description"
                defaultMessage="Accumulate enough Essence to craft powerful Limited Cards in the Card Factory."
              />
            )}
          </LabelM>
        </Vertical>
        {canCraft ? (
          <Button
            size="large"
            color="primary"
            to={`${generatePath(FOOTBALL_CRAFT_RARITY, { rarity: 'limited' })}${QueryString.stringify(
              { redirectUrl: searchParams.get('redirectUrl') },
              {
                addQueryPrefix: true,
              }
            )}`}
          >
            <FormattedMessage
              id="CraftCardDialog.cta"
              defaultMessage="Craft my card"
            />
          </Button>
        ) : (
          <Button size="large" color="secondary" onClick={onClose}>
            <FormattedMessage
              id="EssenceOpeningOnboardingDialog.cta"
              defaultMessage="Got it"
            />
          </Button>
        )}
      </Content>
    </StyledDialog>
  );
};

EssenceOpeningOnboardingDialog.fragments = {
  shardSlotItem: gql`
    fragment EssenceOpeningOnboardingDialog_probabilisticBundleSlotCardShardItem on ProbabilisticBundleSlotCardShardItem {
      id
      quantity
    }
  ` as TypedDocumentNode<EssenceOpeningOnboardingDialog_probabilisticBundleSlotCardShardItem>,
};
